import { useState, useContext } from "react";
import { ref as dbRef, push, set, get, update } from "firebase/database";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { rtdb, storage } from "../../../firebase.config";
import { UserContext } from "../../../context/UserContext";

const useSaveUnit = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useContext(UserContext);

  const uploadPhotos = async (photos) => {
    const urls = [];

    for (const file of photos) {
      if (file.url && !file.originFileObj) {
        urls.push(file.url);
      }

      if (file.originFileObj) {
        const originFile = file.originFileObj;
        const fileRef = storageRef(
          storage,
          `unit_photos/${user.uid}/${Date.now()}-${originFile.name}`
        );
        await uploadBytes(fileRef, originFile);
        const url = await getDownloadURL(fileRef);
        urls.push(url);
      }
    }

    return urls;
  };

  const saveUnit = async (unitData, unitId = null) => {
    setLoading(true);
    setError(null);

    if (!user?.uid) {
      setError("Landlord not authenticated");
      setLoading(false);
      return { success: false, error: "Unauthenticated user" };
    }

    try {
      const { photos } = unitData;
      const uploadedPhotoUrls = await uploadPhotos(photos);

      const payload = {
        ...unitData,
        photos: uploadedPhotoUrls,
        landlordId: user.uid,
        createdAt: new Date().toISOString(),
        status: unitData.status || "draft",
      };

      if (unitId) {
        const unitRef = dbRef(rtdb, `properties/${user.uid}/${unitId}`);
        await update(unitRef, payload);
        setLoading(false);
        return { success: true, id: unitId, updated: true };
      } else {
        const newUnitRef = push(dbRef(rtdb, `properties/${user.uid}`));
        await set(newUnitRef, payload);

        setLoading(false);
        return { success: true, id: newUnitRef.key };
      }
    } catch (err) {
      console.error("Error saving unit:", err);
      setError(err.message || "Something went wrong");
      setLoading(false);
      return { success: false, error: err };
    }
  };

  const getUnitById = async (unitId, landlordId) => {
    if (!unitId || !landlordId) return null;

    try {
      const snapshot = await get(
        dbRef(rtdb, `properties/${landlordId}/${unitId}`)
      );
      if (snapshot.exists()) {
        return { id: unitId, ...snapshot.val() };
      }
      return null;
    } catch (err) {
      console.error("Error fetching unit:", err);
      return null;
    }
  };

  const getAllUnits = async () => {
    setLoading(true);
    if (!user?.uid) return [];

    try {
      const snapshot = await get(dbRef(rtdb, `properties/${user.uid}`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        return Object.entries(data).map(([key, value]) => ({
          id: key,
          ...value,
        }));
      }
      return [];
    } catch (err) {
      console.error("Error fetching properties:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return { saveUnit, getUnitById, getAllUnits, loading, error };
};

export default useSaveUnit;
